<template>
  <transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">
          Swap History Entry
        </h3>

        <p class="text-gray-600 mb-4">
          Choose where to move this history entry:
        </p>

        <!-- Mode Selection: User or Team -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Swap to:
          </label>
          <div class="flex space-x-4">
            <BaseButton
              @click="swapMode = 'user'"
              :variant="swapMode === 'user' ? 'switch' : 'cancel'"
            >
              User
            </BaseButton>
            <BaseButton
              @click="swapMode = 'team'"
              :variant="swapMode === 'team' ? 'switch' : 'cancel'"
            >
              Team
            </BaseButton>
          </div>
        </div>

        <!-- User Selection -->
        <div v-if="swapMode === 'user'" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Select User:
          </label>
          <div v-if="loadingUsers" class="text-gray-500">
            Loading users...
          </div>
          <div v-else-if="users.length === 0" class="text-gray-500">
            No users available
          </div>
          <BaseSelect
            v-else
            v-model="selectedUserId"
            :options="userOptions"
            placeholder="Select a user..."
            :show-placeholder="true"
          />
        </div>

        <!-- Team Selection -->
        <div v-if="swapMode === 'team'" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Select Team:
          </label>
          <div v-if="loadingTeams" class="text-gray-500">
            Loading teams...
          </div>
          <div v-else-if="teams.length === 0" class="text-gray-500">
            No teams available
          </div>
          <BaseSelect
            v-else
            v-model="selectedTeamId"
            :options="teamOptions"
            placeholder="Select a team..."
            :show-placeholder="true"
          />
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3 mt-6">
          <BaseButton
            @click="cancel"
            variant="cancel"
          >
            Cancel
          </BaseButton>
          <BaseButton
            @click="confirm"
            :disabled="!canConfirm"
            variant="switch"
          >
            Swap
          </BaseButton>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { getStudentUsers } from "@/api/users";
import { getUserSession } from "@/live-sessions/api/usersessions";
import { getTeamsForSession } from "@/live-sessions/api/teams";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";

export default {
  components: {
    BaseButton,
    BaseSelect
  },
  props: {
    isOpen: { type: Boolean, required: true },
    sessionId: { type: Number, required: true },
    currentMode: { type: String, default: 'user' }, // 'user' or 'team' - indicates where the history currently is
    currentUserId: { type: Number, default: null }, // The user ID that currently owns the history
    currentTeamId: { type: Number, default: null } // The team ID that currently owns the history
  },
  emits: ["confirm", "cancel"],
  data() {
    return {
      swapMode: 'team', // Default: swap to team
      selectedUserId: null,
      selectedTeamId: null,
      users: [],
      teams: [],
      loadingUsers: false,
      loadingTeams: false
    };
  },
  computed: {
    canConfirm() {
      if (this.swapMode === 'user') {
        return this.selectedUserId !== null;
      } else {
        return this.selectedTeamId !== null;
      }
    },
    userOptions() {
      return this.users
        .filter(user => user.id !== this.currentUserId) // Exclude current user
        .map(user => ({
          value: user.id,
          label: `${user.username} (${user.first_name} ${user.last_name})`
        }));
    },
    teamOptions() {
      return this.teams
        .filter(team => team.id !== this.currentTeamId) // Exclude current team
        .map(team => ({
          value: team.id,
          label: team.name
        }));
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Reset selections when modal opens
        this.selectedUserId = null;
        this.selectedTeamId = null;
        
        // Set default swap mode based on current mode
        this.swapMode = this.currentMode === 'team' ? 'user' : 'team';
        
        // Load data
        this.loadUsers();
        this.loadTeams();
      }
    }
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await getStudentUsers();
        const allUsers = response.data;
        
        // Filter users: only show those with a role in the current session
        const usersWithRoles = [];
        
        await Promise.all(
          allUsers.map(async (user) => {
            try {
              const res = await getUserSession(user.id, this.sessionId);
              const userSession = res.data;
              
              // Only include users with a role
              if (userSession.role_id) {
                usersWithRoles.push(user);
              }
            } catch (err) {
              // User not in this session or no role - skip
            }
          })
        );
        
        // Sort by last name, then first name
        this.users = usersWithRoles.sort((a, b) => {
          const last = a.last_name.localeCompare(b.last_name, "en", { sensitivity: "base" });
          return last !== 0 ? last : a.first_name.localeCompare(b.first_name, "en", { sensitivity: "base" });
        });
      } catch (error) {
        console.error("Failed to load users:", error);
        this.users = [];
      } finally {
        this.loadingUsers = false;
      }
    },
    async loadTeams() {
      this.loadingTeams = true;
      try {
        const response = await getTeamsForSession(this.sessionId);
        this.teams = response.data;
      } catch (error) {
        console.error("Failed to load teams:", error);
        this.teams = [];
      } finally {
        this.loadingTeams = false;
      }
    },
    confirm() {
      if (!this.canConfirm) return;
      
      this.$emit("confirm", {
        mode: this.swapMode,
        userId: this.selectedUserId,
        teamId: this.selectedTeamId
      });
    },
    cancel() {
      this.$emit("cancel");
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
